export type ImageSize = "thumbnail" | "medium" | "full";

// NOTE: a `getCdnImageUrl(url, size)` helper used to live here, but it was a
// no-op — it returned its input unchanged for every branch. Optimized-variant
// selection is handled by `getResponsiveImageUrl` / `getSrcSet` (which read the
// real `cdn_thumbnail/medium/full` fields) and by `resolveListingImageUrl`
// (which maps bare R2 keys onto the assets CDN host). The dead helper had no
// call sites outside this module, so it was removed in the image-asset audit
// rather than left as a trap that silently returns an unoptimized URL.

export function getResponsiveImageUrl(
  image: {
    image_url?: string | null;
    cdn_thumbnail?: string | null;
    cdn_medium?: string | null;
    cdn_full?: string | null;
  },
  size: ImageSize = "medium"
): string {
  switch (size) {
    case "thumbnail":
      return image.cdn_thumbnail || image.image_url || "";
    case "medium":
      return image.cdn_medium || image.image_url || "";
    case "full":
      return image.cdn_full || image.image_url || "";
    default:
      return image.image_url || "";
  }
}

export function resolveListingImageUrl(
  img: {
    url?: string | null;
    image?: string | null;
    r2_path?: string | null;
    cdn_thumbnail?: string | null;
    cdn_medium?: string | null;
    cdn_full?: string | null;
  }
): string {
  // Prefer the already-optimized CDN variants the public site uses (mirrors
  // `getResponsiveImageUrl`). For the remediated fleet these are the working
  // URLs; the legacy `driveshare-assets.com` rebuild below is only a last
  // resort. When any variant exists we never fall through to a dead
  // legacy-origin rebuild.
  if (img.cdn_medium) return img.cdn_medium;
  if (img.cdn_thumbnail) return img.cdn_thumbnail;
  if (img.cdn_full) return img.cdn_full;
  // A full absolute `url` is a known-good origin — prefer it BEFORE rebuilding a
  // `driveshare-assets.com` URL from a bare `r2_path`/`image` key. Otherwise a
  // row whose `r2_path`/`image` holds a stale/dead key, but which also carries a
  // valid absolute `url`, would resolve to the dead rebuild and only render the
  // placeholder — instead of the real photo that `url` still serves.
  if (img.url && (img.url.startsWith("http://") || img.url.startsWith("https://"))) {
    return img.url;
  }
  if (img.r2_path) {
    if (img.r2_path.startsWith("http://") || img.r2_path.startsWith("https://")) {
      return img.r2_path;
    }
    if (img.r2_path.startsWith("/objects/") || img.r2_path.startsWith("uploads/")) {
      return `/api/storage/public-objects/${img.r2_path}`;
    }
    // Bare Cloudflare R2 key (e.g. "cars/123/abc.jpg") — must be served from
    // the assets CDN, otherwise the browser tries to load it as a relative
    // path and gets HTML back. Mirrors the server-side resolvers in
    // routes/listings.ts, routes/users.ts, routes/bookings.ts, etc.
    return `https://driveshare-assets.com/${img.r2_path.replace(/^\/+/, "")}`;
  }
  if (img.image) {
    if (img.image.startsWith("http://") || img.image.startsWith("https://") || img.image.startsWith("/")) {
      return img.image;
    }
    return `https://driveshare-assets.com/${img.image.replace(/^\/+/, "")}`;
  }
  return img.url || "";
}

// A synthesized 1280w candidate (Task #5922) used to be injected here by
// string-replacing `_2400w.webp` → `_1280w.webp` on cdn_full. That assumed every
// car photo whose cdn_full ends in `_2400w.webp` also has a stored `_1280w.webp`
// object. That assumption is FALSE for the subset of the fleet processed before
// the api-server variant pipeline began emitting 1280w (imageProcessor VARIANTS):
// those objects have 400w/800w/2400w but the `_1280w.webp` key 404s. Nothing in
// the image payload tells us whether the 1280w object actually exists, so
// advertising it made high-DPR phones (which select ~1280w) render a BROKEN hero
// while desktop (which selects 2400w) worked — the reported "some cars broken on
// mobile only" bug. We now advertise only widths guaranteed to exist by the real
// cdn_* fields. Task #7090 added `cdn_large` as the explicit 1280w existence
// signal: the API populates it ONLY when the `_1280w.webp` object is verified to
// exist (native pipeline write or HEAD-verified backfill) — never string
// synthesis.
export function getSrcSet(image: {
  image_url?: string | null;
  cdn_thumbnail?: string | null;
  cdn_medium?: string | null;
  cdn_large?: string | null;
  cdn_full?: string | null;
}): string | undefined {
  if (!image.cdn_thumbnail && !image.cdn_medium && !image.cdn_large && !image.cdn_full) {
    return undefined;
  }

  const entries: string[] = [];
  if (image.cdn_thumbnail) entries.push(`${image.cdn_thumbnail} 400w`);
  if (image.cdn_medium) entries.push(`${image.cdn_medium} 800w`);
  if (image.cdn_large) entries.push(`${image.cdn_large} 1280w`);
  if (image.cdn_full) entries.push(`${image.cdn_full} 2400w`);

  return entries.length > 0 ? entries.join(", ") : undefined;
}
